import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";

import themeStyles from "./theme/theme.module.scss";
import styles from "./style.module.scss";

import { attrFor, useHeadOfficeTheme } from "./theme/HeadOfficeThemeContext";
import { hoFontStyle, hoFontVariables } from "./theme/fonts";
import BrandingBand from "./BrandingBand";
import DashboardControls from "./DashboardControls";
import KpiStrip from "./KpiStrip";
import AssignmentPipeline from "./AssignmentPipeline";
import AdoptionGauge from "./AdoptionGauge";
import ReaderCampuses from "./ReaderCampuses";
import CampusComparison from "./CampusComparison";
import TopTeachers from "./TopTeachers";
import TopStudents from "./TopStudents";
import BooksContentAnalytics from "./BooksContentAnalytics";
import TeacherAdoption from "./TeacherAdoption";
import WidgetEmpty from "./_shared/WidgetEmpty";

import CustomizeWidgetsModal from "./modals/CustomizeWidgetsModal";
import DrillDownModal from "./modals/DrillDownModal";
import ExportOptionsModal from "./modals/ExportOptionsModal";
import type { BlurTarget } from "./_shared/exportPdf";
import {
  buildStudentModal,
  buildTeacherModal,
  campusCertificate,
  campusDetailLink,
  studentCertificate,
  studentProfileLink,
  teacherCertificate,
  teacherProfileLink,
  ModalData,
} from "./modals/modalBuilders";
import { DateRange, defaultDateRange } from "./_shared/DateRangePicker";

import {
  CampusDatum,
  COMPARISON_COLS,
  StudentDatum,
  TeacherDatum,
  VIEW_ALL_PAGE_SIZE,
  WIDGET_META,
  WidgetKey,
} from "./data";
import type { ViewAllPage } from "./modals/ViewAllModal";
import { useHeadOfficeData } from "./useHeadOfficeData";
import {
  buildCampusModalFromParts,
  buildStudentModalFromDetail,
  buildTeacherModalFromDetail,
  mapReaderCampuses,
} from "./apiMappers";
import {
  getReaderCampus,
  getReaderCampusDetail,
  getCampusDrilldown,
  getStudentDetail,
  getTeacherDetail,
} from "shared/services/headOfficeService";
import {
  KpiSkeleton,
  GrowthSkeleton,
  MixSkeleton,
  CampusesSkeleton,
  AdoptionSkeleton,
  ListSkeleton,
  ContentSkeleton,
  ComparisonSkeleton,
} from "./Skeleton";

/* The school app defers the two canvas widgets with next/dynamic because they
   must not render on the server. The admin panel is client-only, so they are
   imported directly. */
import ReadingGrowthTrends from "./ReadingGrowthTrends";
import ReadingMix from "./ReadingMix";

interface HeadOfficeDashboardProps {
  schoolName?: string;
}

const defaultShow = () => {
  const s = {} as Record<WidgetKey, boolean>;
  WIDGET_META.forEach((w) => (s[w.key] = true));
  return s;
};

/** The first 5 widgets are fixed — they can never be hidden via Customize. */
const FIXED_WIDGETS: WidgetKey[] = WIDGET_META.slice(0, 5).map((w) => w.key);

export default function HeadOfficeDashboard({
  schoolName = "Ehya School System",
}: HeadOfficeDashboardProps) {
  const { themeKey } = useHeadOfficeTheme();

  const [show, setShow] = useState<Record<WidgetKey, boolean>>(defaultShow);
  const [range, setRange] = useState<DateRange>(defaultDateRange());
  const [mixIdx, setMixIdx] = useState(0);
  const [growthSel, setGrowthSel] = useState<boolean[]>([]);
  const [modal, setModal] = useState<ModalData | null>(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Live data for the selected range. `loading` is per-widget: each section's
  // skeleton clears as soon as its own API responds (refetches on range change).
  const { data, loading, error, refetch } = useHeadOfficeData(range);
  // block Export until every widget has finished loading
  const isLoadingAny = Object.values(loading).some(Boolean);

  const {
    kpis,
    readerCampuses,
    readerMaxBooks,
    teachers,
    students,
    adoption,
    mixDimensions,
    content,
    assignment,
    comparison,
    comparisonTotal,
    teacherAdoption,
    growthRange,
    growthMetrics,
  } = data;

  // Keep the growth metric selection sized to the metrics the API returned.
  useEffect(() => {
    setGrowthSel((prev) => {
      if (prev.length === growthMetrics.length) return prev;
      return growthMetrics.map((_, i) => i === 0);
    });
  }, [growthMetrics.length]);

  /* badge counts only the widgets the modal can actually toggle (the first 5
     are fixed), so it always matches what the user sees in there */
  const activeCount = WIDGET_META.slice(FIXED_WIDGETS.length).filter(
    (m) => show[m.key],
  ).length;

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  const toggleWidget = (key: WidgetKey) => {
    if (FIXED_WIDGETS.includes(key)) return; // first 5 always stay visible
    setShow((s) => ({ ...s, [key]: !s[key] }));
  };
  const resetWidgets = () => setShow(defaultShow());

  const toggleGrowth = (i: number) =>
    setGrowthSel((sel) => {
      const next = sel.slice();
      next[i] = !next[i];
      if (!next.some(Boolean)) next[i] = true; // keep at least one
      return next;
    });

  /* "View All" pager for The Reader Campuses — same endpoint as the card */
  const campusPager = useCallback(
    async (page: number): Promise<ViewAllPage> => {
      const res = await getReaderCampus({
        from: range.from,
        to: range.to,
        per_page: VIEW_ALL_PAGE_SIZE,
        page,
      });
      const d = res?.data?.data ?? {};
      const p = d?.pagination ?? {};
      return {
        rows: mapReaderCampuses(d).campuses,
        total: Number(p.total) || 0,
        lastPage: Number(p.last_page) || 1,
        currentPage: Number(p.current_page) || page,
      };
    },
    [range.from, range.to],
  );

  // Campus drill-down: the per-campus detail for the header/stats plus the
  // three drilldown lenses (class | student | teacher) for the tabs.
  const openCampus = async (c: CampusDatum, rank = 0) => {
    if (!c.campus_id) return;
    // only the network's top campus is certified
    const certificate = rank === 1 ? campusCertificate(c, win) : undefined;
    showToast("Loading campus…");
    try {
      const lens = (type: "class" | "student" | "teacher") =>
        getCampusDrilldown(c.campus_id as number, {
          type,
          ...win,
          per_page: 5,
          page: 1,
        })
          .then((r) => r?.data?.data)
          .catch(() => null);

      const [res, classes, students, teachers] = await Promise.all([
        getReaderCampusDetail(c.campus_id),
        lens("class"),
        lens("student"),
        lens("teacher"),
      ]);
      const detail = res?.data?.data;
      if (detail) {
        setModal({
          ...buildCampusModalFromParts(detail, classes, students, teachers),
          // link straight through to the campus's own detail page
          profile: campusDetailLink(c.campus_id),
          certificate,
        });
      }
    } catch (e) {
      showToast("Couldn't load campus details");
    }
  };
  // Student drill-down: fetch the per-student detail (same range), then build
  // the modal. Falls back to the row data if the id/API isn't available.
  const openStudent = async (s: StudentDatum, rank = 0) => {
    // link straight through to the student's own school-panel page
    const profile = studentProfileLink(s.student_id);
    // only the network's top reader is certified
    const certificate = rank === 1 ? studentCertificate(s, win) : undefined;
    if (!s.student_id) {
      setModal({ ...buildStudentModal(s), profile, certificate });
      return;
    }
    showToast("Loading student…");
    try {
      const res = await getStudentDetail(s.student_id, range);
      const detail = res?.data?.data;
      setModal({
        ...(detail ? buildStudentModalFromDetail(detail) : buildStudentModal(s)),
        profile,
        certificate,
      });
    } catch (e) {
      showToast("Couldn't load student details");
    }
  };

  // Teacher drill-down: fetch the per-teacher detail (same range), then build
  // the modal. Falls back to the row data if the id/API isn't available.
  const openTeacher = async (t: TeacherDatum, rank = 0) => {
    // link straight through to the teacher's own school-panel page
    const profile = teacherProfileLink(t.teacher_id);
    // only the network's top teacher is certified
    const certificate = rank === 1 ? teacherCertificate(t, win) : undefined;
    if (!t.teacher_id) {
      setModal({ ...buildTeacherModal(t), profile, certificate });
      return;
    }
    showToast("Loading teacher…");
    try {
      const res = await getTeacherDetail(t.teacher_id, range);
      const detail = res?.data?.data;
      setModal({
        ...(detail
          ? buildTeacherModalFromDetail(detail, t.name)
          : buildTeacherModal(t)),
        profile,
        certificate,
      });
    } catch (e) {
      setModal({ ...buildTeacherModal(t), profile, certificate });
    }
  };

  // Export asks first what to blur, then exports the dashboard container itself
  // to a real PDF (looks like the page, no sidebar/header, no print chrome).
  const openExport = () => {
    if (isLoadingAny) return; // wait until every widget has data
    setModal(null);
    setExportOpen(true);
  };
  const exportPdf = async (blur: BlurTarget[]) => {
    setExportOpen(false);
    showToast("Generating export…");
    // let any modal unmount so it isn't captured
    await new Promise((r) => setTimeout(r, 300));
    try {
      const el = document.getElementById("ho-report-root");
      if (!el) return;
      const { exportElementToPdf } = await import("./_shared/exportPdf");
      // a blurred school name shouldn't survive in the file name either
      const fileName =
        blur.includes("school")
          ? "Head Office Report.pdf"
          : `${schoolName} — Head Office Report.pdf`;
      await exportElementToPdf(el, fileName, {
        excludeWidgets: [],
        blur,
      });
      showToast("Export downloaded (PDF)");
    } catch (e) {
      console.error("PDF export failed", e);
      showToast("Couldn't export the dashboard");
    }
  };
  const generateCertificate = () =>
    showToast("Certificate generated (PDF)");

  const mixActiveIdx = mixDimensions.length
    ? Math.min(mixIdx, mixDimensions.length - 1)
    : 0;

  // Reporting window for the current range — passed to widgets whose "View All"
  // modal paginates the full campus list from the API for the same range.
  const win = range;

  // Show whatever rows the API returns (even zeros for a slow period); only
  // fall back to the not-found state when a response is genuinely empty.
  // (Adoption is empty only when there are no enrolled students at all.)
  const hasReaders = readerCampuses.length > 0;
  const hasContent = !!content && content.campuses.length > 0;
  const hasAdoption = !!adoption && adoption.enrolled > 0;
  const hasComparison = comparison.length > 0;
  const hasTeacherAdoption = teacherAdoption.length > 0;
  const hasGrowth = growthMetrics.length > 0 && !!growthRange;

  // helper: render a widget cell only when enabled
  const cell = (
    key: WidgetKey,
    span: string,
    order: number,
    node: React.ReactNode,
    stretch = false,
  ) =>
    show[key] && node ? (
      <div
        key={key}
        data-ho-widget={key}
        className={classNames(styles.cell, span, stretch && styles.stretch)}
        style={{ order }}
      >
        {node}
      </div>
    ) : null;

  return (
    <div
      id="ho-report-root"
      className={classNames(themeStyles.scope, styles.dashboard, hoFontVariables)}
      style={hoFontStyle}
      data-ho-theme={attrFor(themeKey)}
    >
      <div className={styles.topRow}>
        <BrandingBand name={schoolName} />
        <DashboardControls
          range={range}
          onRangeChange={setRange}
          activeCount={activeCount}
          onCustomize={() => setCustomizeOpen(true)}
          onExport={openExport}
          exportDisabled={isLoadingAny}
        />
      </div>

      {error ? (
        <div className={styles.stateBox}>
          <span>{error}</span>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {cell(
            "kpi",
            styles.span12,
            1,
            loading.kpi ? (
              <KpiSkeleton />
            ) : kpis.length ? (
              <KpiStrip kpis={kpis} />
            ) : (
              <WidgetEmpty title="Network at a Glance" />
            ),
          )}

          {cell(
            "growth",
            styles.span8,
            2,
            loading.growth ? (
              <GrowthSkeleton />
            ) : !hasGrowth ? (
              <WidgetEmpty
                title="Reading Growth Trends"
                subtitle="Books & pages completed over time"
              />
            ) : growthRange && growthSel.length === growthMetrics.length ? (
              <ReadingGrowthTrends
                range={growthRange}
                metrics={growthMetrics}
                selected={growthSel}
                onToggle={toggleGrowth}
                themeKey={themeKey}
              />
            ) : (
              <GrowthSkeleton />
            ),
            true,
          )}


          {cell(
            "mix",
            styles.span4,
            3,
            loading.mix ? (
              <MixSkeleton />
            ) : mixDimensions.length > 0 ? (
              <ReadingMix
                dimensions={mixDimensions}
                activeIdx={mixActiveIdx}
                onChange={setMixIdx}
              />
            ) : (
              <WidgetEmpty title="Reading Mix" subtitle="By genre & language" />
            ),
            true,
          )}

          {cell(
            "campuses",
            styles.span8,
            4,
            loading.campuses ? (
              <CampusesSkeleton />
            ) : hasReaders ? (
              <ReaderCampuses
                campuses={readerCampuses.slice(0, 4)}
                maxBooks={readerMaxBooks}
                onOpenCampus={openCampus}
                fetchPage={campusPager}
                viewAllSubtitle="Network-wide — all campuses"
              />
            ) : (
              <WidgetEmpty
                title="The Reader Campuses"
                subtitle="Ranked by reading performance"
                hint="No campuses have reading activity in this period yet."
              />
            ),
            true,
          )}

          {cell(
            "adoption",
            styles.span4,
            5,
            loading.adoption ? (
              <AdoptionSkeleton />
            ) : hasAdoption && adoption ? (
              <AdoptionGauge
                percent={adoption.percent}
                caption={adoption.caption}
                deltaPct={adoption.deltaPct}
                deltaLabel="vs previous period"
                ageAppropriate={adoption.ageAppropriate}
              />
            ) : (
              <WidgetEmpty title="Adoption Rate" />
            ),
            true,
          )}

          {cell(
            "teachers",
            styles.span6,
            6,
            loading.teachers ? (
              <ListSkeleton rows={4} />
            ) : teachers.length ? (
              <TopTeachers
                teachers={teachers.slice(0, 4)}
                win={win}
                onOpenTeacher={openTeacher}
              />
            ) : (
              <WidgetEmpty title="Top Teachers" />
            ),
          )}

          {cell(
            "students",
            styles.span6,
            7,
            loading.students ? (
              <ListSkeleton rows={4} />
            ) : students.length ? (
              <TopStudents
                students={students.slice(0, 4)}
                onOpenStudent={openStudent}
                win={win}
              />
            ) : (
              <WidgetEmpty title="Top Students" />
            ),
          )}


          {/* Books & Content Analytics hidden — Assignment Pipeline takes this slot.
          {cell(
            "content",
            styles.span6,
            8,
            loading.content ? (
              <ContentSkeleton />
            ) : hasContent && content ? (
              <BooksContentAnalytics
                summary={content.summary}
                campuses={content.campuses}
                maxBooks={content.maxBooks}
                win={win}
              />
            ) : (
              <WidgetEmpty
                title="Books & Content Analytics"
                subtitle="What the network is reading, and whether it fits"
              />
            ),
            true,
          )}
          */}

          {cell(
            "teacherAdoption",
            styles.span6,
            9,
            loading.teacherAdoption ? (
              <ListSkeleton rows={5} />
            ) : hasTeacherAdoption ? (
              <TeacherAdoption campuses={teacherAdoption} win={win} />
            ) : (
              <WidgetEmpty
                title="Teacher Adoption"
                subtitle="Active teaching staff by campus"
              />
            ),
            true,
          )}

          {cell(
            "comparison",
            styles.span12,
            10,
            loading.comparison ? (
              <ComparisonSkeleton />
            ) : hasComparison ? (
              <CampusComparison
                cols={COMPARISON_COLS}
                campuses={comparison}
                total={comparisonTotal}
                win={win}
              />
            ) : (
              <WidgetEmpty
                title="Campus Comparison"
                subtitle="Key metrics side by side"
              />
            ),
            true,
          )}

          {/* Assignment Pipeline — left of Teacher Adoption, in the slot the
              Books & Content Analytics card used to occupy. Data rides on
              book-content; toggleable via Customize Widgets. */}
          {show.assignment && (
            <div
              data-ho-widget="assignment"
              className={classNames(styles.cell, styles.span6, styles.stretch)}
              style={{ order: 8 }}
            >
              {loading.content ? (
                <MixSkeleton />
              ) : assignment ? (
                <AssignmentPipeline
                  statuses={assignment.statuses}
                  total={assignment.total}
                  donePct={assignment.donePct}
                />
              ) : (
                <WidgetEmpty
                  title="Assignment Pipeline"
                  subtitle="Assignment status across the network"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------------- Modals ---------------- */}
      {modal && (
        <DrillDownModal
          data={modal}
          onClose={() => setModal(null)}
          onGenerateReport={openExport}
          onGenerateCertificate={generateCertificate}
        />
      )}

      {exportOpen && (
        <ExportOptionsModal
          showCampus
          onClose={() => setExportOpen(false)}
          onExport={exportPdf}
        />
      )}

      {customizeOpen && (
        <CustomizeWidgetsModal
          show={show}
          onToggle={toggleWidget}
          onReset={resetWidgets}
          onClose={() => setCustomizeOpen(false)}
        />
      )}

      {/* ---------------- Toast ---------------- */}
      {toast && (
        <div className={classNames(styles.toast, "ho-no-print")}>
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M8.5 12.5 11 15l4.5-5" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
}
